import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { MAX_TRANSCRIPT_RANGE_IN_MS } from '~/swr-hooks/clips/transcript/constants';
import {
  type SearchedItemsCache,
  type SearchedTranscriptSegment,
  type SearchTranscriptCache,
  type TranscriptSearchedItem,
} from '~/swr-hooks/clips/transcript/search/types';
import { useSearchPrivateTranscript } from '~/swr-hooks/clips/transcript/search/useSearchPrivateTranscript';
import { type ClipTranscriptCache, type TimeRange } from '~/swr-hooks/clips/transcript/types';
import { useGetPrivateClipTranscriptSegments } from '~/swr-hooks/clips/transcript/useGetPrivateClipTranscriptSegments';
import { getTranscriptWordsFromSearchResults } from '~/swr-hooks/clips/transcript/utils/getTranscriptWordsFromSearchResults';

export const getSearchedItemsForPrivateTranscriptKey = (clipId: string, search: string) => [
  'SEARCHED_ITEMS_PRIVATE_TRANSCRIPT',
  { clipId, search },
];

const emptySearchResult: SearchedItemsCache = { data: [], hasMore: false, total: 0 };

/**
 * This hook uses {@link useSearchPrivateTranscript} to search transcript for a given phrase
 * and then converts results to items with time ranges
 * Converted data is stored in react-query cache
 */
export const useSearchedItemsForPrivateTranscript = ({
  search,
  clipId,
  videoDurationMs,
}: {
  clipId: string;
  search: string;
  videoDurationMs: number;
}) => {
  const queryClient = useQueryClient();
  const key = useMemo(() => getSearchedItemsForPrivateTranscriptKey(clipId, search), [clipId, search]);

  const {
    key: searchResultsKey,
    isFetching,
    searchTranscriptInRange,
  } = useSearchPrivateTranscript({
    search,
    clipId,
    videoDurationMs,
    enabled: false,
  });

  const { fetchTranscriptForRange, key: segmentsKey } = useGetPrivateClipTranscriptSegments({
    clipId,
    videoDurationMs,
    enabled: false,
  });

  const updateSearchedItemsCache = useCallback(
    (searchResults: SearchTranscriptCache, segments: ClipTranscriptCache) => {
      if (!searchResults || !segments) {
        queryClient.setQueryData(key, emptySearchResult);
        return;
      }

      const flattenedSegments = segments.data.flatMap((segment) => segment.data);

      const searchedItems = searchResults?.data.reduce<TranscriptSearchedItem[]>(
        (collected, resultsForRange, rangeIndex) => {
          const collectedForRange = resultsForRange.data.reduce<TranscriptSearchedItem[]>((acc, searchResult) => {
            const segment = flattenedSegments.find((s) => s.id === searchResult.id);

            if (!!segment) {
              const ranges = getTranscriptWordsFromSearchResults({
                searchPhrase: search,
                segment,
                enrichedText: searchResult.text,
              });
              const searchedItems = ranges.map<TranscriptSearchedItem>((range) => ({
                ...range,
                type: 'result',
              }));
              acc.push(...searchedItems);
            }
            return acc;
          }, []);

          collected.push(...collectedForRange);
          const isLastRange = rangeIndex === searchResults.data.length - 1;
          const isLastResult = resultsForRange.endTimeMs >= videoDurationMs;

          if (!isLastResult) {
            collected.push({
              type: 'empty',
              startTimeMs: resultsForRange.endTimeMs,
              endTimeMs: isLastRange ? videoDurationMs : searchResults.data[rangeIndex + 1].startTimeMs,
            });
          }
          return collected;
        },
        [],
      );

      queryClient.setQueryData(key, {
        data: searchedItems,
        hasMore: searchResults.hasMore,
        total: searchResults.total,
      });
    },
    [key, queryClient, search, videoDurationMs],
  );

  const { data: searchedItems } = useQuery({
    queryKey: key,
    queryFn: () => queryClient.getQueryData<SearchedItemsCache>(key) ?? null,
  });

  const fetchSearchResultsForRange = useCallback(
    async ({ startTimeMs, endTimeMs }: TimeRange) => {
      const currentSearchResults = queryClient.getQueryData<SearchTranscriptCache>(searchResultsKey);
      const currentSearchResultsIds =
        currentSearchResults?.data.reduce<Set<string>>((acc, range) => {
          range.data.forEach((result) => acc.add(result.id));
          return acc;
        }, new Set()) ?? new Set();

      const searchResults = await searchTranscriptInRange({ startTimeMs, endTimeMs });
      if (!searchResults) return;
      // For each result we need segment data, so we have to fetch if we are missing any
      // To ensure that we have necessary data we have to find new search results first
      const newSearchResults = searchResults.data.reduce<SearchedTranscriptSegment[]>((acc, range) => {
        range.data.forEach((result) => {
          if (!currentSearchResultsIds.has(result.id)) {
            acc.push(result);
          }
        });
        return acc;
      }, []);

      let currentSegments = queryClient.getQueryData<ClipTranscriptCache>(segmentsKey);
      const currentSegmentsIds =
        currentSegments?.data.reduce<Set<string>>((acc, range) => {
          range.data.forEach((segment) => acc.add(segment.id));
          return acc;
        }, new Set()) ?? new Set();

      // Then find if there are any missing segments - segments that exist in search results but not in current segments
      const missingSegments = newSearchResults.filter((result) => !currentSegmentsIds.has(result.id));

      if (missingSegments.length > 0) {
        // If there are missing segments, fetch segments from startTime to max range
        const firstSegmentStartTime = missingSegments[0].startTimeMs;
        const endTime = Math.min(firstSegmentStartTime + MAX_TRANSCRIPT_RANGE_IN_MS, videoDurationMs);
        const startTime = Math.max(0, endTime - MAX_TRANSCRIPT_RANGE_IN_MS);
        currentSegments = await fetchTranscriptForRange({ startTimeMs: startTime, endTimeMs: endTime });
      }

      if (!!currentSegments && !!searchResults) {
        updateSearchedItemsCache(searchResults, currentSegments);
      }

      return searchResults;
    },
    [
      fetchTranscriptForRange,
      queryClient,
      searchResultsKey,
      searchTranscriptInRange,
      segmentsKey,
      updateSearchedItemsCache,
      videoDurationMs,
    ],
  );

  return {
    data: searchedItems,
    isFetching,
    total: searchedItems?.total ?? 0,
    fetchSearchResultsForRange,
  };
};
